<template>
    <div class="w-full">
        <div class="w-full text-base border-b p-1 mb-2 tracking-widest flex justify-between items-center">
            <span>Issues</span>
            <span v-if="warrantyStatus" class="text-xs px-2 py-0.5 rounded" :class="warrantyBadgeClass">
                Warranty: {{ warrantyStatusLabel }}
            </span>
        </div>

        <div class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
            Don't paste passwords, recovery keys, or other credentials into issue text or attachments.
        </div>

        <button
            type="button"
            @click="reportDialogOpen = true"
            class="rounded px-3 py-1 text-sm bg-dark-primary text-white hover:bg-dark-secondary mb-3"
        >
            Report new issue
        </button>

        <div v-if="!issues.length" class="text-sm text-gray-500 italic p-2">
            No issues reported yet.
        </div>

        <ul v-else class="space-y-3">
            <li
                v-for="issue in issues"
                :key="issue.id"
                class="border rounded p-3"
                :class="issue.is_open ? 'border-red-300' : 'border-gray-200 bg-gray-50'"
            >
                <div class="flex justify-between items-start">
                    <div class="flex-1">
                        <div class="font-semibold text-sm">{{ issue.title }}</div>
                        <div class="text-xs text-gray-600">
                            Reported {{ formatDate(issue.reported_at) }} by
                            <span v-if="issue.reported_by">{{ issue.reported_by.first_name }} {{ issue.reported_by.last_name }}</span>
                            <span v-else>?</span>
                            <span v-if="issue.severity"> · severity {{ issue.severity }}</span>
                        </div>
                        <div v-if="issue.description" class="text-sm text-gray-800 mt-1 whitespace-pre-line">
                            {{ issue.description }}
                        </div>
                    </div>
                    <div class="flex flex-col items-end ml-2">
                        <span class="text-xs px-2 py-0.5 rounded" :class="statusBadgeClass(issue.status)">
                            {{ issue.status }}
                        </span>
                        <select
                            v-if="canChangeStatus"
                            class="mt-1 text-xs border rounded"
                            :value="issue.status"
                            @change="changeStatus(issue, $event.target.value)"
                        >
                            <option value="open">open</option>
                            <option value="in_progress">in_progress</option>
                            <option value="resolved">resolved</option>
                            <option value="wont_fix">wont_fix</option>
                            <option value="closed">closed</option>
                        </select>
                    </div>
                </div>

                <ul v-if="issue.updates && issue.updates.length" class="mt-2 space-y-1 border-t pt-2">
                    <li v-for="u in issue.updates" :key="u.id" class="text-xs">
                        <span class="text-gray-500">
                            {{ formatDate(u.created_at) }} ·
                            <span v-if="u.author_user">{{ u.author_user.first_name }}</span>
                            <span v-else>?</span>
                            <span v-if="u.update_type" class="ml-1 italic">[{{ u.update_type }}]</span>:
                        </span>
                        <span class="whitespace-pre-line">{{ u.body }}</span>
                    </li>
                </ul>

                <div class="mt-2 flex space-x-1">
                    <input
                        v-model="newUpdateBody[issue.id]"
                        type="text"
                        placeholder="Add an update…"
                        class="flex-1 border rounded p-1 text-xs"
                        @keyup.enter="addUpdate(issue)"
                    />
                    <select v-model="newUpdateType[issue.id]" class="border rounded text-xs">
                        <option value="comment">comment</option>
                        <option value="repair_attempt">repair</option>
                        <option value="resolution">resolution</option>
                    </select>
                    <button
                        type="button"
                        @click="addUpdate(issue)"
                        :disabled="!newUpdateBody[issue.id] || busy"
                        class="px-2 rounded bg-dark-primary text-white text-xs disabled:bg-gray-300"
                    >Add</button>
                </div>
            </li>
        </ul>

        <base-modal-small v-if="reportDialogOpen" @close="reportDialogOpen = false">
            <template #header>Report new issue</template>
            <template #body>
                <div class="space-y-2 text-sm p-2">
                    <div class="flex flex-col">
                        <label class="text-gray-500">Title*</label>
                        <input type="text" required v-model="reportForm.title" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Description</label>
                        <textarea v-model="reportForm.description" class="border rounded p-1" rows="4" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Severity</label>
                        <select v-model="reportForm.severity" class="border rounded p-1">
                            <option :value="null">(none)</option>
                            <option value="low">low</option>
                            <option value="medium">medium</option>
                            <option value="high">high</option>
                        </select>
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Photo (optional, camera on touch)</label>
                        <input type="file" accept="image/*" capture="environment" @change="onPhotoSelected" />
                    </div>
                    <div class="flex justify-end space-x-2 pt-2">
                        <button type="button" @click="reportDialogOpen = false" class="px-3 py-1 border rounded">Cancel</button>
                        <button type="button" :disabled="!reportForm.title || busy" @click="submitReport" class="px-3 py-1 rounded bg-dark-primary text-white">Report</button>
                    </div>
                </div>
            </template>
        </base-modal-small>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import { formatDate } from '@/composables/formatDate';
import {
    listInventoryIssues,
    createInventoryIssue,
    updateInventoryIssue,
    createIssueUpdate,
    uploadIssueAttachment,
} from '@/services/Inventory';

const props = defineProps(['inventoryItem']);
const emit = defineEmits(['refetch']);

const store = useStore();
const $toast = useToast();
const issues = ref([]);
const busy = ref(false);
const reportDialogOpen = ref(false);
const reportForm = ref({ title: '', description: '', severity: null, photoFile: null });
const newUpdateBody = ref({});
const newUpdateType = ref({});

const canChangeStatus = computed(() => {
    const perms = store.getters['auth/showUserPermissions'] || {};
    return perms.isDev || perms.isSuperuser || perms.canEditOrAddInventory || perms.editOrAddInventory;
});

const warrantyStatus = computed(() => props.inventoryItem?.warranty_status);
const warrantyStatusLabel = computed(() => ({
    active: 'active', expiring_soon: 'expiring soon',
    expired: 'expired', unknown: 'unknown',
}[warrantyStatus.value] || warrantyStatus.value));
const warrantyBadgeClass = computed(() => ({
    active: 'bg-green-100 text-green-800',
    expiring_soon: 'bg-amber-100 text-amber-800',
    expired: 'bg-red-100 text-red-800',
    unknown: 'bg-gray-200 text-gray-700',
}[warrantyStatus.value] || 'bg-gray-100 text-gray-700'));

function statusBadgeClass(s) {
    return {
        open: 'bg-red-100 text-red-700',
        in_progress: 'bg-amber-100 text-amber-700',
        resolved: 'bg-green-100 text-green-700',
        wont_fix: 'bg-gray-200 text-gray-700',
        closed: 'bg-gray-200 text-gray-700',
    }[s] || 'bg-gray-100 text-gray-700';
}

async function loadIssues() {
    if (!props.inventoryItem) return;
    try {
        const token = store.state.auth.token;
        const resp = await listInventoryIssues(token, `?inventory_item=${props.inventoryItem.id}`);
        issues.value = resp.data?.results || resp.data || [];
    } catch (e) {
        $toast.error(`Could not load issues: ${describe(e)}`);
    }
}

onMounted(loadIssues);
watch(() => props.inventoryItem?.id, loadIssues);

function onPhotoSelected(e) {
    reportForm.value.photoFile = e.target.files[0] || null;
}

async function submitReport() {
    busy.value = true;
    try {
        const token = store.state.auth.token;
        const { photoFile, ...payload } = reportForm.value;
        const resp = await createInventoryIssue({
            ...payload,
            inventory_item: props.inventoryItem.id,
        }, token);
        const issue = resp.data;
        if (photoFile) {
            try {
                await uploadIssueAttachment({ issueId: issue.id, file: photoFile }, token);
            } catch (e) {
                $toast.warning(`Issue reported, but photo upload failed: ${describe(e)}`);
            }
        }
        $toast.success('Issue reported.');
        reportDialogOpen.value = false;
        reportForm.value = { title: '', description: '', severity: null, photoFile: null };
        await loadIssues();
        emit('refetch');
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

async function changeStatus(issue, newStatus) {
    busy.value = true;
    try {
        const token = store.state.auth.token;
        await updateInventoryIssue(issue.id, { status: newStatus }, token);
        $toast.success(`Issue status: ${newStatus}.`);
        await loadIssues();
        emit('refetch');
    } catch (e) {
        $toast.error(describe(e));
        await loadIssues(); // resync on failure
    } finally {
        busy.value = false;
    }
}

async function addUpdate(issue) {
    const body = (newUpdateBody.value[issue.id] || '').trim();
    if (!body) return;
    busy.value = true;
    try {
        const token = store.state.auth.token;
        await createIssueUpdate({
            inventory_issue: issue.id,
            body,
            update_type: newUpdateType.value[issue.id] || 'comment',
        }, token);
        newUpdateBody.value[issue.id] = '';
        await loadIssues();
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

function describe(e) {
    if (e?.response?.data) {
        if (typeof e.response.data === 'string') return e.response.data;
        return Object.values(e.response.data).flat().join(' · ');
    }
    return e?.message || String(e);
}
</script>
